'use client'

import { useCallback, useEffect, useState } from 'react'

import {
  getPortalTutorialStatus,
  PORTAL_TUTORIAL_STATUS_EVENT,
  type PortalTutorialStatus,
  setPortalTutorialStatus,
} from '@common/core/utils/cookies'

export function usePortalTutorialPersistence() {
  const [status, setStatus] = useState<PortalTutorialStatus | null>(() =>
    typeof document === 'undefined' ? null : getPortalTutorialStatus()
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    const syncFromCookie = () => {
      setStatus(getPortalTutorialStatus())
    }

    const handleStatusChange = (event: Event) => {
      const customEvent = event as CustomEvent<PortalTutorialStatus | null>
      setStatus(customEvent.detail ?? getPortalTutorialStatus())
    }

    window.addEventListener(PORTAL_TUTORIAL_STATUS_EVENT, handleStatusChange)
    window.addEventListener('focus', syncFromCookie)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') syncFromCookie()
    })

    return () => {
      window.removeEventListener(PORTAL_TUTORIAL_STATUS_EVENT, handleStatusChange)
      window.removeEventListener('focus', syncFromCookie)
    }
  }, [])

  const markCompleted = useCallback(() => {
    setPortalTutorialStatus('completed')
    setStatus('completed')
  }, [])

  const markDismissed = useCallback(() => {
    setPortalTutorialStatus('dismissed')
    setStatus('dismissed')
  }, [])

  const shouldAutoStart = status === null

  return {
    status,
    shouldAutoStart,
    markCompleted,
    markDismissed,
  }
}
